/* =======================================================================
   Visit — Sezione/Pagina Contatti
   =======================================================================
   Mostra: indirizzo, orari (in tabella), telefoni (chiamabili al click) e mappa.
   I dati sono in src/data/copy.js → sezione "visit".
   ======================================================================= */
import { useLang } from "@/context/LanguageContext";

export default function Visit({ copy }) {
  const { tr } = useLang();
  const phones = copy.visit.phones;
  const hours = copy.visit.hours_rows;

  return (
    <section
      id="visita"
      data-testid="visit"
      className="bg-[#F7F4EB] text-[#2C2C2A] py-24 lg:py-36"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-8">
            <p className="text-[0.72rem] tracking-[0.32em] uppercase text-[#737554] mb-6">
              — {tr(copy.visit.overline)}
            </p>
            <h2 className="font-serif font-light text-[clamp(2.4rem,5vw,4.6rem)] leading-[1.02] tracking-tight">
              {tr(copy.visit.title)}
            </h2>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-px bg-[#D7D0B7] border border-[#D7D0B7]">
          {/* Address + Map directions */}
          <Block
            label={tr(copy.visit.address_label)}
            className="lg:col-span-5"
            testid="visit-address"
          >
            <p className="font-serif text-2xl lg:text-[1.7rem] leading-snug whitespace-pre-line">
              {tr(copy.visit.address)}
            </p>
            <a
              data-testid="visit-directions"
              href="https://maps.google.com/?q=Via+Antonio+Gramsci+55,+Rosignano+Marittimo,+LI,+Italy"
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-3 mt-7 bg-[#BC472A] hover:bg-[#a93b22] text-[#F7F4EB] px-6 py-3.5 rounded-md text-sm uppercase tracking-[0.22em] transition-colors shadow-sm hover:shadow-md"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {tr(copy.visit.cta_directions)}
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </a>
          </Block>

          {/* Hours table */}
          <Block
            label={tr(copy.visit.hours_label)}
            className="lg:col-span-4"
            testid="visit-hours"
          >
            <ul className="divide-y divide-[#D7D0B7]/70">
              {hours.map((row, i) => (
                <li
                  key={i}
                  data-testid={`hours-${i}`}
                  className={`flex items-baseline justify-between py-2 ${
                    row.closed ? "text-[#737554]" : "text-[#2C2C2A]"
                  }`}
                >
                  <span className="font-serif text-lg lg:text-xl">
                    {tr(row.day)}
                  </span>
                  <span
                    className={`text-[0.95rem] font-light tracking-wide ${
                      row.closed ? "italic" : ""
                    }`}
                  >
                    {typeof row.time === "string" ? row.time : tr(row.time)}
                  </span>
                </li>
              ))}
            </ul>
          </Block>

          {/* Phones */}
          <Block
            label={tr(copy.visit.contact_label)}
            className="lg:col-span-3"
            testid="visit-phones"
          >
            <ul className="space-y-3">
              {phones.map((p, i) => (
                <li key={p.number}>
                  <a
                    data-testid={`phone-link-${i}`}
                    href={`tel:+39${p.number.replace(/\s/g, "")}`}
                    className="group flex flex-col gap-0.5 hover:text-[#BC472A] transition-colors"
                  >
                    <span className="text-[0.65rem] tracking-[0.28em] uppercase text-[#737554] group-hover:text-[#BC472A]/80">
                      {tr(p.label)}
                    </span>
                    <span className="font-serif text-xl">{p.number}</span>
                  </a>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex flex-col gap-2">
              {phones.map((p, i) => (
                <a
                  key={`btn-${p.number}`}
                  data-testid={`call-btn-${i}`}
                  href={`tel:+39${p.number.replace(/\s/g, "")}`}
                  className="group inline-flex items-center gap-3 bg-[#2C2C2A] hover:bg-[#BC472A] text-[#F7F4EB] px-4 py-2.5 rounded-md text-[0.7rem] uppercase tracking-[0.2em] transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  {tr({ it: "Chiama", en: "Call" })} · {tr(p.label)}
                </a>
              ))}
            </div>
          </Block>

          {/* Services */}
          <Block
            label={tr(copy.visit.services_label)}
            className="lg:col-span-12"
            testid="visit-services"
          >
            <p className="text-base font-light leading-relaxed max-w-3xl">
              {tr(copy.visit.services)}
            </p>
          </Block>
        </div>

        {/* Map iframe */}
        <div className="mt-16 relative h-[360px] lg:h-[460px] w-full overflow-hidden rounded-md border border-[#D7D0B7]">
          <iframe
            data-testid="visit-map"
            title="Mappa Le Simpatiche Canaglie"
            src="https://www.google.com/maps?q=Via+Antonio+Gramsci+55,+Rosignano+Marittimo,+LI,+Italy&output=embed"
            className="absolute inset-0 h-full w-full grayscale-[40%]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}

function Block({ label, children, className = "", testid }) {
  return (
    <div data-testid={testid} className={`bg-[#F7F4EB] p-8 lg:p-10 ${className}`}>
      <p className="text-[0.7rem] tracking-[0.3em] uppercase text-[#737554] mb-5">{label}</p>
      {children}
    </div>
  );
}
